import { FontFamily, useFontFamily } from '@/hooks/useFontFamily';
import { cn } from '@/lib/utils/tailwind.utils';
import { cva, VariantProps } from 'class-variance-authority';
import React, { ElementType, JSX } from 'react';

const textVariants = cva(cn(), {
  variants: {
    variant: {
      default: 'text-primary group-hover:text-primary',
      heading1: 'text-primary group-hover:text-primary',
      heading2: 'text-secondary',
      heading3: 'text-primary group-hover:text-primary',
      heading4: 'text-quaternary',
      heading5: 'text-quinary',
      heading6: 'text-senary',
      paragraph: 'text-primary group-hover:text-primary',
      body1: 'text-body1',
      body2: 'text-body2',
      muted: 'text-muted-foreground',
      modern: 'text-modern',
      link: 'text-secondary underline hover:underline',
      destructive: 'text-destructive',
      announce: 'text-announce',
      brandPrimary: 'text-brand-primary group-hover:text-brand-primary/90 data-[state=inactive]:text-muted-foreground',
    },
    size: {
      xxs: 'text-[10px]',
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
      '6xl': 'text-6xl',
      '7xl': 'text-7xl',
      '8xl': 'text-8xl',
      '9xl': 'text-9xl',
      '10xl': 'text-10xl',
    },
    weight: {
      thin: 'font-thin',
      light: 'font-light',
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    tracking: {
      tight: 'tracking-tight',
      normal: 'tracking-normal',
      wide: 'tracking-wide',
      widest: 'tracking-widest',
    },
  },
  compoundVariants: [],
  defaultVariants: {
    variant: 'default',
    size: null,
    weight: null,
    tracking: null,
  },
});

type TextTransform = 'uppercase' | 'lowercase' | 'capitalize' | 'none';
type TextAlign = 'left' | 'center' | 'right' | 'justify';

type TextVariantProps = VariantProps<typeof textVariants>;
export type TypographyVariant = NonNullable<TextVariantProps['variant']>;
type RenderAsElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'a' | 'label';

const VARIANT_MAP: Record<TypographyVariant, RenderAsElement> = {
  default: 'h3',
  heading1: 'h1',
  heading2: 'h2',
  heading3: 'h3',
  heading4: 'h4',
  heading5: 'h5',
  heading6: 'h6',
  paragraph: 'p',
  body1: 'span',
  body2: 'span',
  muted: 'span',
  modern: 'span',
  link: 'span',
  destructive: 'span',
  announce: 'span',
  brandPrimary: 'h1',
};

export type TypographyProps = React.HTMLAttributes<HTMLElement> &
  TextVariantProps & {
    ref?: React.Ref<HTMLElement>;
    renderAs?: RenderAsElement;
    transform?: TextTransform;
    align?: TextAlign;
    isLoading?: boolean;
    ariaLabel?: string;
    accessibilityAutoFocus?: boolean;
    fontFamily?: FontFamily;
    id?: string;
  };

export const Typography = ({
  ref,
  id,
  className,
  variant = 'default',
  size = 'base',
  tracking = 'normal',
  weight = 'normal',
  transform = 'none',
  fontFamily = 'roboto',
  align,
  renderAs,
  ariaLabel,
  accessibilityAutoFocus = false,
  isLoading = false,
  ...rest
}: TypographyProps): JSX.Element => {
  const styleFontFamily = useFontFamily(fontFamily);
  const props: TypographyProps = {
    style: {
      textTransform: transform,
      textAlign: align,
    },
    ...rest,
  };

  // render as variant or override with renderAs
  const Component: ElementType = renderAs ?? VARIANT_MAP[variant ?? 'default'];

  return (
    <Component
      // @ts-expect-error - Component is a valid element type
      ref={ref}
      id={id}
      className={cn(textVariants({ variant, size, weight, tracking }), className, styleFontFamily)}
      aria-label={ariaLabel}
      autoFocus={accessibilityAutoFocus}
      {...props}
    />
  );
};
